/**
 * Navigation store for sidebar and navigation state.
 *
 * Supports both static (main) and dynamic (study-specific) navigation.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  fetchStudyNavigation,
  fetchStudyNavigationTree,
  NavigationItem as ApiNavigationItem,
  StudyPage,
  StudyModuleConfig,
} from '@/lib/studyConfigApi'
import { fetchMyAccessiblePages, AccessiblePage } from '@/lib/userManagementApi'

export interface NavigationItem {
  id: string
  label: string
  icon?: string
  path?: string
  children?: NavigationItem[]
  badge?: number | string
  moduleKey?: string
  requiresStudy?: boolean
  requiredRoles?: string[]
  requiredFeature?: string | null
  navType?: 'menu' | 'page' | 'section'
  color?: string | null
  aiCapabilities?: string[]
}

interface NavigationState {
  // Sidebar state
  isSidebarCollapsed: boolean
  isSidebarOpen: boolean // For mobile

  // Expanded menu items (for nested menus) - using Record for better Zustand reactivity
  expandedItems: Record<string, boolean>

  // Dynamic navigation (loaded from backend)
  mainNavigation: NavigationItem[]
  studyNavigation: NavigationItem[]
  studyModules: StudyModuleConfig[]
  isLoadingStudyNav: boolean
  loadedStudyId: string | null

  // Page access control
  accessiblePages: AccessiblePage[]
  accessiblePageSlugs: Set<string>
  isLoadingAccessiblePages: boolean
  hasPageRestrictions: boolean // true if any restrictions are configured

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setSidebarOpen: (open: boolean) => void
  toggleExpandedItem: (itemId: string) => void
  setExpandedItem: (itemId: string, expanded: boolean) => void
  collapseAll: () => void

  // Dynamic navigation actions
  loadStudyNavigation: (studyId: string) => Promise<void>
  clearStudyNavigation: () => void
  setMainNavigation: (items: NavigationItem[]) => void

  // Page access actions
  loadAccessiblePages: () => Promise<void>
  clearAccessiblePages: () => void
  isPageAccessible: (slug: string) => boolean
}

/**
 * Convert API navigation item to frontend NavigationItem format.
 */
function convertApiNavItem(item: ApiNavigationItem, prefix: string = ''): NavigationItem {
  const pathPrefix = item.requiresStudy ? '/study/:studyId' : ''

  return {
    id: `${prefix}${item.slug}`,
    label: item.name,
    icon: item.icon || undefined,
    path: item.children?.length ? undefined : `${pathPrefix}/${item.slug}`,
    requiresStudy: item.requiresStudy,
    children: item.children?.map((child) => convertApiNavItem(child, `${prefix}${item.slug}-`)),
    moduleKey: item.slug,
  }
}

/**
 * Convert StudyPage to frontend NavigationItem format.
 * This uses the per-study navigation stored in study_pages table.
 *
 * Note: Feature gating is handled server-side - pages requiring disabled features
 * are filtered out before being sent to the frontend.
 */
function convertStudyPageToNavItem(page: StudyPage, prefix: string = 'study-'): NavigationItem {
  const hasChildren = page.children && page.children.length > 0
  const isMenu = page.navType === 'menu'
  const isSection = page.navType === 'section'

  return {
    id: `${prefix}${page.pageKey}`,
    label: page.name,
    icon: page.icon || undefined,
    // Sections and menus don't have paths, only pages do
    // Use routePath from DB if set, otherwise fall back to pageKey
    path: (isMenu || isSection) ? undefined : `/study/:studyId/${page.routePath || page.pageKey}`,
    requiresStudy: true,
    children: isSection
      ? undefined
      : hasChildren
        ? page.children!.map((child) => convertStudyPageToNavItem(child, `${prefix}${page.pageKey}-`))
        : undefined,
    moduleKey: page.pageKey,
    requiredFeature: page.requiredFeature,
    navType: page.navType,
    color: page.color,
    aiCapabilities: page.aiCapabilities || [],
  }
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      isSidebarCollapsed: false,
      isSidebarOpen: false,
      expandedItems: {} as Record<string, boolean>,

      // Dynamic navigation state
      mainNavigation: [],
      studyNavigation: [],
      studyModules: [],
      isLoadingStudyNav: false,
      loadedStudyId: null,

      // Page access control state
      accessiblePages: [],
      accessiblePageSlugs: new Set<string>(),
      isLoadingAccessiblePages: false,
      hasPageRestrictions: false,

      toggleSidebar: () =>
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ isSidebarCollapsed: collapsed }),

      setSidebarOpen: (open) => set({ isSidebarOpen: open }),

      toggleExpandedItem: (itemId) =>
        set((state) => ({
          expandedItems: {
            ...state.expandedItems,
            [itemId]: !state.expandedItems[itemId],
          },
        })),

      setExpandedItem: (itemId, expanded) =>
        set((state) => ({
          expandedItems: {
            ...state.expandedItems,
            [itemId]: expanded,
          },
        })),

      collapseAll: () => set({ expandedItems: {} }),

      // Page access actions
      loadAccessiblePages: async () => {
        if (get().isLoadingAccessiblePages) return

        set({ isLoadingAccessiblePages: true })
        try {
          const pages = await fetchMyAccessiblePages()
          const slugs = new Set(pages.map((p) => p.slug))

          // Determine if restrictions are in place
          // If we got a limited set of pages, restrictions are active
          // We can't easily tell from the response alone, so we'll assume
          // any non-empty response means restrictions are being applied
          // (The backend returns all pages if no restrictions exist)
          set({
            accessiblePages: pages,
            accessiblePageSlugs: slugs,
            hasPageRestrictions: pages.length > 0,
            isLoadingAccessiblePages: false,
          })
        } catch (error) {
          console.error('Failed to load accessible pages:', error)
          set({ isLoadingAccessiblePages: false })
        }
      },

      clearAccessiblePages: () =>
        set({
          accessiblePages: [],
          accessiblePageSlugs: new Set<string>(),
          hasPageRestrictions: false,
        }),

      isPageAccessible: (slug: string) => {
        const state = get()
        // If no restrictions are configured, all pages are accessible
        if (!state.hasPageRestrictions || state.accessiblePageSlugs.size === 0) {
          return true
        }
        return state.accessiblePageSlugs.has(slug)
      },

      // Dynamic navigation actions
      loadStudyNavigation: async (studyId: string) => {
        // Skip if already loaded for this study
        if (get().loadedStudyId === studyId && !get().isLoadingStudyNav) {
          return
        }

        set({ isLoadingStudyNav: true })

        try {
          // Fetch study-specific navigation from study_pages table
          const studyPages = await fetchStudyNavigationTree(studyId)

          // Convert StudyPage objects to NavigationItem format
          const studyNav = studyPages.map((page) => convertStudyPageToNavItem(page))

          // Also fetch the main navigation (for non-study routes)
          // This uses the old API which reads from navigation_items
          let mainNav: NavigationItem[] = []
          let modules: StudyModuleConfig[] = []
          try {
            const response = await fetchStudyNavigation(studyId)
            mainNav = response.mainNavigation.map((item) => convertApiNavItem(item))
            modules = response.modules
          } catch {
            // If old API fails, just use empty arrays
            console.warn('Failed to load main navigation, using defaults')
          }

          set({
            mainNavigation: mainNav,
            studyNavigation: studyNav,
            studyModules: modules,
            loadedStudyId: studyId,
            isLoadingStudyNav: false,
          })
        } catch (error) {
          console.error('Failed to load study navigation:', error)
          set({ isLoadingStudyNav: false })
          // Fall back to default navigation on error
        }
      },

      clearStudyNavigation: () =>
        set({
          studyNavigation: [],
          studyModules: [],
          loadedStudyId: null,
        }),

      setMainNavigation: (items) => set({ mainNavigation: items }),
    }),
    {
      name: 'sage-navigation',
      partialize: () => ({
        // isSidebarCollapsed now comes from userPreferencesStore (backend-synced)
        // Don't persist dynamic navigation or expandedItems
      }),
    }
  )
)

// Default navigation structure - ONLY non-study items
// Study-specific navigation comes exclusively from the database (study_pages table)
export const defaultNavigation: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/dashboard',
    requiresStudy: false,
    requiredRoles: ['system_admin'],  // Only system admins can see the admin dashboard
  },
  {
    id: 'studies',
    label: 'Studies',
    icon: 'FolderOpen',
    path: '/studies',
    requiresStudy: false,
  },
  {
    id: 'admin-users-security',
    label: 'Users & Security',
    icon: 'ShieldAlert',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
    children: [
      {
        id: 'admin-users',
        label: 'User Management',
        path: '/admin/users',
        icon: 'Users',
      },
      {
        id: 'admin-roles',
        label: 'Role Management',
        path: '/admin/roles',
        icon: 'Shield',
      },
      {
        id: 'admin-security',
        label: 'Security Settings',
        path: '/admin/security',
        icon: 'Lock',
      },
      {
        id: 'admin-audit',
        label: 'Audit Logs',
        path: '/admin/audit-logs',
        icon: 'FileText',
      },
    ],
  },
  {
    id: 'admin-platform-config',
    label: 'Platform Configuration',
    icon: 'Cog',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
    children: [
      {
        id: 'admin-branding',
        label: 'Branding',
        path: '/admin/branding',
        icon: 'Palette',
      },
      {
        id: 'admin-notifications',
        label: 'Notifications',
        path: '/admin/notifications',
        icon: 'Bell',
      },
    ],
  },
  {
    id: 'admin-ai-suite',
    label: 'AI Suite',
    icon: 'Brain',
    path: '/admin/ai-suite',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
  },
  {
    id: 'admin-narrative-settings',
    label: 'Narrative Settings',
    icon: 'FileCheck',
    path: '/admin/narrative-settings',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
  },
  {
    id: 'admin-modules-data',
    label: 'Modules & Data',
    icon: 'Blocks',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
    children: [
      {
        id: 'admin-modules',
        label: 'Module Library',
        path: '/admin/modules',
        icon: 'Blocks',
      },
      {
        id: 'admin-column-labels',
        label: 'Column Labels',
        path: '/admin/column-labels',
        icon: 'Tags',
      },
      {
        id: 'admin-external-files',
        label: 'External Files',
        path: '/admin/external-files',
        icon: 'FileSpreadsheet',
      },
      {
        id: 'admin-ct-library',
        label: 'Controlled Terminology',
        path: '/admin/ct-library',
        icon: 'BookOpen',
      },
      {
        id: 'admin-coding-library',
        label: 'Coding Library',
        path: '/admin/coding-library',
        icon: 'Dna',
      },
    ],
  },
  {
    id: 'admin-r-pipeline',
    label: 'R Pipeline',
    icon: 'Terminal',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
    children: [
      {
        id: 'admin-r-packages',
        label: 'R Package Manager',
        path: '/admin/r-packages',
        icon: 'Package',
      },
      {
        id: 'admin-r-functions',
        label: 'R Function Library',
        path: '/admin/r-functions',
        icon: 'Code2',
      },
    ],
  },
  {
    id: 'admin-system',
    label: 'System',
    icon: 'Activity',
    requiresStudy: false,
    requiredRoles: ['system_admin'],
    children: [
      {
        id: 'admin-system-monitoring',
        label: 'System Monitoring',
        path: '/admin/system',
        icon: 'Activity',
      },
      {
        id: 'admin-change-control',
        label: 'Update Change Control',
        path: '/admin/update-change-control',
        icon: 'GitBranch',
      },
      {
        id: 'admin-compliance-docs',
        label: 'Document Management',
        path: '/admin/compliance-docs',
        icon: 'FileCheck',
      },
    ],
  },
]
